<template>
    <!-- Mortality detail modal: the figures behind a section's Dynamic Mortality rate. A headline
         card carries the age and both windows' rates (the active view is highlighted and switchable
         in-modal), a secondary card carries the calculation inputs, and a stock chart (PigTallyChart,
         shared with WaterDetailModal) shows the day-by-day pig tally with 12 months of history
         behind a navigator. Shell (teleport/overlay/header/prev-next/Escape+Arrow keys) lives in
         DetailModalShell, shared with WeightDetailModal/WaterDetailModal. -->
    <DetailModalShell :section="section" :sections="sections" @close="emit('close')" @navigate="onNavigate" @handle-alarm="onHandleAlarm">
        <template #default="{ section: activeSection }">
            <!-- Headline card: age + both windows' rates; the active view is highlighted and
                 each rate block is a button that selects its window as the modal's view. -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <div class="flex items-center justify-between gap-2">
                    <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.title') }}
                    </h3>
                    <!-- Opens the shared Targets modal (ManageObjectivesModal), pre-focused on this
                         section's own Farm - visible to everyone, no role gate (active-groups itself is
                         still pre-release; gating happens at that level, not per-affordance). -->
                    <button
                        type="button"
                        class="inline-flex shrink-0 items-center gap-1 rounded-md border border-primary-700 px-2 py-1 text-[11px] font-medium text-primary-700 transition-colors hover:bg-gray-50"
                        @click="emit('editTarget', activeSection)"
                    >
                        <Settings class="h-3 w-3" aria-hidden="true" />
                        {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.editTarget') }}
                    </button>
                </div>
                <div class="mt-2 grid grid-cols-3 gap-2 tabular-nums">
                    <div class="rounded-md px-2 py-1.5">
                        <p class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.tables.headings.age') }}
                        </p>
                        <p class="mt-0.5 text-[22px] font-extrabold leading-tight tracking-tight text-gray-900">
                            {{ activeSection.day !== null ? `${$t('views.monitoring.activeGroups.texts.week')} ${activeSection.week}` : placeholder }}
                        </p>
                        <p v-if="activeSection.day !== null" class="text-[11px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.texts.day') }} {{ activeSection.day }}
                        </p>
                    </div>
                    <button
                        type="button"
                        class="cursor-pointer rounded-md px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
                        :class="statBlockClass('rolling')"
                        :aria-pressed="modalView === 'rolling'"
                        @click="modalView = 'rolling'"
                    >
                        <p class="text-[10px] font-bold uppercase tracking-[0.5px]">
                            <span :class="statLabelClass('rolling')">
                                {{ $t('views.monitoring.activeGroups.mortalityView.rolling') }}
                            </span>
                        </p>
                        <p class="mt-0.5 flex items-baseline gap-1 text-[22px] font-extrabold leading-tight tracking-tight" :class="rateClass(activeSection.mortalityAtOrAboveTarget)">
                            <span>{{ activeSection.mortalityPercent !== null ? `${$n(activeSection.mortalityPercent, 'decimal')} %` : placeholder }}</span>
                            <!-- Pig-diff figure: same colour/format helpers as the active-groups Døde cell's
                                 own (+N)/(-N) indicator (objectiveDisplay.ts) - own sign convention from the %
                                 beside it. Hidden when null, not a placeholder. -->
                            <span
                                v-if="activeSection.mortalityRolling?.pigs_to_target != null"
                                class="text-[13px] font-semibold"
                                :class="pigsToTargetColourClass(activeSection.mortalityRolling.pigs_to_target)"
                            >({{ formatPigsToTarget(activeSection.mortalityRolling.pigs_to_target) }})</span>
                        </p>
                        <p v-if="activeSection.mortalityTargetPercent !== null" class="text-[11px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.target') }} {{ targetLabel(activeSection.mortalityAtOrAboveTarget) }}
                        </p>
                        <!-- Week-over-week dead-count trend: raw counts summed from the tally series
                             (never recomputed rates). Arrow points at the direction of change; up is
                             worse. Hidden until both 7-day windows are fully covered. -->
                        <p v-if="deadTrend" class="flex items-center gap-1 text-[11px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.previousWindow') }}: {{ deadTrend.previous }}
                            <TrendingUp v-if="deadTrend.current > deadTrend.previous" class="h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden="true" />
                            <TrendingDown v-else-if="deadTrend.current < deadTrend.previous" class="h-3.5 w-3.5 shrink-0 text-green-700" aria-hidden="true" />
                        </p>
                    </button>
                    <button
                        type="button"
                        class="cursor-pointer rounded-md px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
                        :class="statBlockClass('cumulative')"
                        :aria-pressed="modalView === 'cumulative'"
                        @click="modalView = 'cumulative'"
                    >
                        <p class="text-[10px] font-bold uppercase tracking-[0.5px]">
                            <span :class="statLabelClass('cumulative')">
                                {{ $t('views.monitoring.activeGroups.mortalityView.cumulative') }}
                            </span>
                        </p>
                        <p class="mt-0.5 flex items-baseline gap-1 text-[22px] font-extrabold leading-tight tracking-tight" :class="rateClass(activeSection.mortalityCumulativeAtOrAboveTarget)">
                            <span>{{ activeSection.mortalityCumulativePercent !== null ? `${$n(activeSection.mortalityCumulativePercent, 'decimal')} %` : placeholder }}</span>
                            <span
                                v-if="activeSection.mortalityCumulative?.pigs_to_target != null"
                                class="text-[13px] font-semibold"
                                :class="pigsToTargetColourClass(activeSection.mortalityCumulative.pigs_to_target)"
                            >({{ formatPigsToTarget(activeSection.mortalityCumulative.pigs_to_target) }})</span>
                        </p>
                        <p v-if="activeSection.mortalityTargetPercent !== null" class="text-[11px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.target') }} {{ targetLabel(activeSection.mortalityCumulativeAtOrAboveTarget) }}
                        </p>
                    </button>
                </div>
                <!-- Explains a suppressed percent (null under the active view) — e.g. no active Study
                     Period, or one whose days-in-piggery or pig-count inputs aren't ready yet. -->
                <p
                    v-if="mortalityPercentForView(activeSection, modalView) === null && suppressionReasonText"
                    class="mt-3 rounded-md bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800"
                >
                    {{ suppressionReasonText }}
                </p>
            </div>

            <!-- Calculation inputs per window; the active view's column is highlighted. -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                    {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.figuresTitle') }}
                </h3>
                <table class="mt-1 w-full text-sm tabular-nums">
                    <thead>
                        <tr class="border-b border-gray-200 text-[10px] font-bold uppercase tracking-[0.5px]">
                            <th class="py-1.5 text-left font-bold" />
                            <th class="px-3 py-1.5 text-right" :class="columnClass('rolling')">
                                <span :class="statLabelClass('rolling')">
                                    {{ $t('views.monitoring.activeGroups.mortalityView.rolling') }}
                                </span>
                            </th>
                            <th class="px-3 py-1.5 text-right" :class="columnClass('cumulative')">
                                <span :class="statLabelClass('cumulative')">
                                    {{ $t('views.monitoring.activeGroups.mortalityView.cumulative') }}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr>
                            <td class="py-2 text-xs text-gray-400">{{ $t('views.monitoring.activeGroups.modals.mortalityDetail.dead') }}</td>
                            <td class="px-3 py-2 text-right text-[15px] font-semibold" :class="columnClass('rolling')">
                                {{ activeSection.mortalityRolling?.dead ?? placeholder }}
                            </td>
                            <td class="px-3 py-2 text-right text-[15px] font-semibold" :class="columnClass('cumulative')">
                                {{ activeSection.mortalityCumulative?.dead ?? placeholder }}
                            </td>
                        </tr>
                        <tr>
                            <td class="py-2 text-xs text-gray-400">{{ $t('views.monitoring.activeGroups.modals.mortalityDetail.avgPigs') }}</td>
                            <td class="px-3 py-2 text-right text-[15px] font-semibold" :class="columnClass('rolling')">
                                {{ activeSection.mortalityRolling ? $n(activeSection.mortalityRolling.avg_pigs, 'decimal') : placeholder }}
                            </td>
                            <td class="px-3 py-2 text-right text-[15px] font-semibold" :class="columnClass('cumulative')">
                                {{ activeSection.mortalityCumulative ? $n(activeSection.mortalityCumulative.avg_pigs, 'decimal') : placeholder }}
                            </td>
                        </tr>
                        <tr>
                            <td class="py-2 text-xs text-gray-400">{{ $t('views.monitoring.activeGroups.modals.mortalityDetail.windowDays') }}</td>
                            <td class="px-3 py-2 text-right text-[15px] font-semibold" :class="columnClass('rolling')">
                                {{ windowDaysLabel(activeSection.mortalityRolling) }}
                            </td>
                            <td class="px-3 py-2 text-right text-[15px] font-semibold" :class="columnClass('cumulative')">
                                {{ windowDaysLabel(activeSection.mortalityCumulative) }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- The math behind the headline number: the active window's inputs composing
                     into the same % shown above (delivered values reused, never recomputed). -->
                <div v-if="activeWindow" class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-gray-100 px-3 py-2 tabular-nums">
                    <div class="text-center">
                        <p class="text-sm font-bold leading-tight text-gray-800">{{ activeWindow.dead }}</p>
                        <p class="text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.formulaDead') }}
                        </p>
                    </div>
                    <span class="text-sm font-semibold text-gray-300">÷</span>
                    <div class="text-center">
                        <p class="text-sm font-bold leading-tight text-gray-800">{{ $n(activeWindow.avg_pigs, 'decimal') }}</p>
                        <p class="text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.formulaAvgPigs') }}
                        </p>
                    </div>
                    <!-- The days-in-piggery ÷ window factor is what makes the two windows comparable:
                         avg pigs is a stock and dead is a flow, so this converts the observed rate into
                         deaths per pig per stay. It stays meaningful when the window is *longer* than a
                         stay (the multiplier just drops below 1) — a section observed for 331 days has
                         turned its herd over many times, and the un-normalised rate would be comparable
                         to nothing. So the factor always renders. -->
                    <span class="text-sm font-semibold text-gray-300">×</span>
                    <div class="text-center">
                        <p class="text-sm font-bold leading-tight text-gray-800">
                            {{ activeSection.mortalityDaysInPiggery !== null ? $n(activeSection.mortalityDaysInPiggery, 'decimal') : placeholder }}<span class="text-gray-400"> / </span>{{ activeWindow.window_days }}
                        </p>
                        <p class="text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.formulaFactor') }}
                        </p>
                    </div>
                    <span class="text-sm font-semibold text-gray-300">=</span>
                    <p class="text-base font-extrabold leading-tight" :class="rateClass(activeAtOrAboveTarget)">
                        {{ activePercent !== null ? `${$n(activePercent, 'decimal')} %` : placeholder }}
                    </p>
                </div>
                <!-- Value and provenance of the days-in-piggery figure — the factor above when it
                     applied, and the length the window is being compared against when it didn't. -->
                <p v-if="activeSection.mortalityDaysInPiggery !== null" class="mt-1.5 text-[11px] text-gray-400">
                    {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.daysInPiggery') }}:
                    {{ $n(activeSection.mortalityDaysInPiggery, 'decimal') }} —
                    {{
                        activeSection.mortalityDaysInPiggeryDerived
                            ? $t('views.monitoring.activeGroups.modals.mortalityDetail.daysInPiggeryDerived')
                            : $t('views.monitoring.activeGroups.modals.mortalityDetail.daysInPiggeryStandard')
                    }}
                </p>
            </div>

            <!-- Pig tally: the current Production Group shown by default, 12 months of history reachable
                 through the navigator. Same chart as WaterDetailModal's pig-tally section. -->
            <PigTallyChart :section-id="activeSection.id" :day="activeSection.day" :directory-local-now="activeSection.directoryLocalNow" />
        </template>
    </DetailModalShell>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Settings, TrendingDown, TrendingUp } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import { usePigTallySeries } from '@/plugins/store/actions/queries/active-groups';
import type { MortalityWindow } from '@/types/SectionLiveKpi';
import { mortalityAtOrAboveTargetForView, mortalityPercentForView, type MortalityView, type SectionRow } from './sectionRow';
import { INDEX_COLOUR_NEUTRAL } from './indexColour';
import { formatPigsToTarget, pigsToTargetColourClass } from '@/views/dashboard/overview/objectives/objectiveDisplay';
import DetailModalShell from './DetailModalShell.vue';
import PigTallyChart from './PigTallyChart.vue';

const placeholder = '—';

const props = defineProps<{
    // The row whose Døde cell was clicked (null = closed).
    section: SectionRow | null;
    // The page-level view — seeds the modal's own scope selection on each open.
    view: MortalityView;
    // All rows whose Døde cell can open this modal, in the page's display order — drives prev/next
    // navigation. The parent stays in control: navigation only emits the target row.
    sections: SectionRow[];
}>();

const emit = defineEmits<{
    close: [];
    navigate: [section: SectionRow];
    handleAlarm: [section: SectionRow];
    editTarget: [section: SectionRow];
}>();

function onNavigate(target: SectionRow): void {
    emit('navigate', target);
}

function onHandleAlarm(target: SectionRow): void {
    emit('handleAlarm', target);
}

// Feeds the week-over-week dead-count trend below (deadTrend) - not chart rendering, which is
// PigTallyChart's own concern. Tanstack Query dedupes this against PigTallyChart's identical
// query key, so this doesn't cost a second request.
const sectionId = computed(() => props.section?.id ?? null);
const { data: tallyData } = usePigTallySeries(sectionId);
const tallySeries = computed(() => tallyData.value ?? []);

// Same red/green scheme as the Døde cell: red at or above target, green below, neutral (no colour)
// when the target comparison itself is unknown - that must not silently read as "all good".
function rateClass(atOrAboveTarget: boolean | null): string {
    if (atOrAboveTarget === null) return INDEX_COLOUR_NEUTRAL;
    return atOrAboveTarget ? 'text-red-600' : 'text-green-700';
}

// The modal's own scope selection: seeded from the page-level view on open, then switched by
// clicking the stat blocks. Survives prev/next navigation and ignores page-toggle changes while
// open; each open re-adopts the page-level view (the component stays mounted between opens).
const modalView = ref<MortalityView>(props.view);

watch(
    () => props.section,
    (section, previous) => {
        if (section && !previous) modalView.value = props.view;
    }
);

// The stat block / table column matching the modal's view gets a visible fill, and its label
// echoes the page toggle's active state (brand pill) so the selection is self-explanatory.
// Inactive blocks get a lighter hover fill so they read as clickable.
function statBlockClass(view: MortalityView): string {
    return modalView.value === view ? 'bg-gray-100' : 'hover:bg-gray-100/60';
}

function statLabelClass(view: MortalityView): string {
    return modalView.value === view ? 'inline-block rounded bg-primary-700 px-1.5 py-0.5 leading-none text-white' : 'text-gray-400';
}

function columnClass(column: MortalityView): string {
    return modalView.value === column ? 'bg-gray-100 text-gray-900' : 'text-gray-600';
}

// The active window's inputs feed the formula strip under the calculation table.
const activeWindow = computed(() => (modalView.value === 'cumulative' ? props.section?.mortalityCumulative : props.section?.mortalityRolling) ?? null);
const activePercent = computed(() => (props.section ? mortalityPercentForView(props.section, modalView.value) : null));
const activeAtOrAboveTarget = computed(() => (props.section ? mortalityAtOrAboveTargetForView(props.section, modalView.value) : null));

// Target with a pass/fail marker per window; dash when no target is configured.
function targetLabel(atOrAboveTarget: boolean | null): string {
    const target = props.section?.mortalityTargetPercent ?? null;
    if (target === null) return placeholder;
    const marker = atOrAboveTarget === null ? '' : atOrAboveTarget ? ' ✕' : ' ✓';
    return `${i18n.global.n(target, 'decimal')} %${marker}`;
}

function windowDaysLabel(window: MortalityWindow | null): string {
    if (!window) return placeholder;
    return `${window.window_days} ${i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.daysUnit')}`;
}

// Maps the backend-computed suppression reason to a user-facing explanation of why the active
// view's percent is a dash. Empty when a figure is available (nothing to explain). Keyed by the
// union rather than `string`, so adding a wire value without a message here is a compile error
// instead of a silently blank panel.
type SuppressionReason = NonNullable<SectionRow['mortalitySuppressionReason']>;

const suppressionReasonMessages: Record<SuppressionReason, string> = {
    NoActiveProductionGroup: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.noActiveProductionGroup',
    DaysInPiggeryUnavailable: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.daysInPiggeryUnavailable',
    InsufficientPigsInWindow: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.insufficientPigsInWindow',
    PigMovementsNotRegisteredPerSection: 'views.monitoring.activeGroups.modals.mortalityDetail.suppressionReasons.pigMovementsNotRegisteredPerSection',
};

const suppressionReasonText = computed(() => {
    const reason = props.section?.mortalitySuppressionReason;
    const key = reason ? suppressionReasonMessages[reason] : undefined;
    return key ? i18n.global.t(key) : '';
});

// Week-over-week dead trend: raw dead counts summed over the series' last 7 daily points vs the
// 7 before them (the series ends today). Null until both windows are fully covered.
const deadTrend = computed(() => {
    const series = tallySeries.value;
    if (series.length < 14) return null;
    const sumDead = (points: typeof series) => points.reduce((sum, point) => sum + point.dead, 0);
    return {
        current: sumDead(series.slice(-7)),
        previous: sumDead(series.slice(-14, -7)),
    };
});
</script>
